import { classByArmorType } from '@/data/character-class';
import { Constants } from '@/data/constants';
import {
    isGatheringProfession,
    isCraftingProfession,
    professionSlugToId,
} from '@/data/professions';
import { ArmorType } from '@/enums/armor-type';
import { Faction } from '@/enums/faction';
import { QuestStatus } from '@/enums/quest-status';
import { Role } from '@/enums/role';
import { settingsState } from '@/shared/state/settings.svelte';
import { wowthingData } from '@/shared/stores/data';
import { parseBooleanQuery } from '@/shared/utils/boolean-parser';
import { userState } from '@/user-home/state/user';
import type { Settings } from '@/shared/stores/settings/types';
import type { Character } from '@/types';

type FilterFunc = (char: Character) => boolean;

const _cache: Record<string, string[][]> = {};

export function useCharacterFilter(
    settings: Settings,
    filterFunc: FilterFunc,
    char: Character,
    filterString: string
): boolean {
    let result = true;
    if (filterString?.length >= 2) {
        const filterLower = filterString.toLocaleLowerCase();
        let partArrays: string[][];
        if (_cache[filterLower] !== undefined) {
            partArrays = _cache[filterLower];
        } else {
            try {
                partArrays = _cache[filterLower] = parseBooleanQuery(filterLower);
            } catch {
                partArrays = _cache[filterLower] = [];
            }
        }
        // console.log(char.name, partArrays);

        if (partArrays.length === 0) {
            return true;
        }

        const partCache: Record<string, boolean> = {};
        result = partArrays.some((parts) =>
            parts.every(
                (outerPart) =>
                    (partCache[outerPart] ||=
                        (function (part: string) {
                            if (char.name.toLocaleLowerCase().indexOf(part) >= 0) {
                                return true;
                            }

                            if (part === 'any') {
                                return true;
                            }

                            // Level
                            let match = part.match(/^(level|lvl)(<|<=|=|>=|>)(\d+)$/);
                            if (match) {
                                return compareValues(
                                    match[2].toString(),
                                    char.level,
                                    parseInt(match[3])
                                );
                            }

                            // Last seen (in days)
                            match = part.match(/^seen(<|<=|=|>=|>)(\d+)$/);
                            if (match) {
                                const now = Math.floor(Date.now() / 1000);
                                const days = parseInt(match[2]);
                                const diff = (now - char.lastSeenAddonUnix) / 86400;
                                return compareValues(match[1].toString(), diff, days);
                            }

                            // Item level
                            match = part.match(/^(itemlevel|ilevel|ilvl)(<|<=|=|>=|>)(\d+)$/);
                            if (match) {
                                return compareValues(
                                    match[2].toString(),
                                    char.equippedItemLevel,
                                    parseInt(match[3])
                                );
                            }

                            // Quests
                            match = part.match(/^(no)?quest=(.+)$/);
                            if (match) {
                                const questString = match[2].toString();
                                const questId = parseInt(questString);

                                let hasQuest = false;

                                const charQuests = userState.quests.characterById.get(char.id); // userQuestData.characters[char.id];
                                if (charQuests) {
                                    if (isNaN(questId)) {
                                        const matchingQuests = Array.from(
                                            charQuests.progressQuestByKey?.entries()
                                        )
                                            .filter(
                                                ([key]) => key.toLocaleLowerCase() === questString
                                            )
                                            .map(([, value]) => value);
                                        hasQuest =
                                            matchingQuests.length === 1 &&
                                            matchingQuests[0].status === QuestStatus.Completed;
                                    } else {
                                        hasQuest = charQuests.hasQuestById.has(questId);
                                    }
                                }

                                return match[1]?.toString() === 'no' ? !hasQuest : hasQuest;
                            }

                            // Account tag
                            match = part.match(/^accounttag=(.*)$/);
                            if (match) {
                                const accountTag = match[1].toString();
                                return (
                                    settings.accounts?.[char.accountId].tag.toLocaleLowerCase() ==
                                    accountTag
                                );
                            }

                            // Tag
                            match = part.match(/^tag=(.*)$/);
                            if (match) {
                                const tagName = match[1].toString().toLocaleLowerCase();
                                const tag = (settings.tags || []).find(
                                    (t) => t.name.toLocaleLowerCase() === tagName
                                );
                                return (
                                    tag &&
                                    ((settings.characters.flags?.[char.id] || 0) & (1 << tag.id)) >
                                        0
                                );
                            }

                            // Realm slug
                            match = part.match(/^realm=(.+)$/);
                            if (match) {
                                const slug = match[1].toString();
                                return char.realm.slug === slug;
                            }

                            // Faction
                            if (part === 'alliance') {
                                return char.faction === Faction.Alliance;
                            } else if (part === 'horde') {
                                return char.faction === Faction.Horde;
                            } else if (part === 'netural') {
                                return char.faction === Faction.Neutral;
                            }

                            // Race slug
                            const raceSlug = ['dracthyr', 'earthen', 'pandaren'].includes(part)
                                ? `${part}${char.faction}`
                                : part;
                            const maybeRace = wowthingData.static.characterRaceBySlug.get(raceSlug);
                            if (maybeRace) {
                                return char.raceId === maybeRace.id;
                            }

                            // Class slug
                            let classSlug = part;
                            if (classSlug === 'dh') {
                                classSlug = 'demon-hunter';
                            } else if (classSlug === 'dk') {
                                classSlug = 'death-knight';
                            }
                            const maybeClass =
                                wowthingData.static.characterClassBySlug.get(classSlug);
                            if (maybeClass) {
                                return char.classId === maybeClass.id;
                            }

                            // Armor type
                            if (part === 'cloth') {
                                return classByArmorType[ArmorType.Cloth].includes(char.classId);
                            } else if (part === 'leather') {
                                return classByArmorType[ArmorType.Leather].includes(char.classId);
                            } else if (part === 'mail') {
                                return classByArmorType[ArmorType.Mail].includes(char.classId);
                            } else if (part === 'plate') {
                                return classByArmorType[ArmorType.Plate].includes(char.classId);
                            }

                            // Specializations
                            if (part === 'tank') {
                                const spec = wowthingData.static.characterSpecializationById.get(
                                    char.activeSpecId
                                );
                                return spec?.role === Role.Tank;
                            } else if (part === 'heal' || part === 'healer' || part === 'heals') {
                                const spec = wowthingData.static.characterSpecializationById.get(
                                    char.activeSpecId
                                );
                                return spec?.role === Role.Healer;
                            } else if (part === 'dps' || part === 'deeps') {
                                const spec = wowthingData.static.characterSpecializationById.get(
                                    char.activeSpecId
                                );
                                return (
                                    spec?.role === Role.MeleeDps || spec?.role === Role.RangedDps
                                );
                            }

                            // Mythic+ score
                            match = part.match(/^m\+((<|<=|=|>=|>)(\d+))?$/);
                            if (match) {
                                const mythicPlusScore =
                                    char.mythicPlusSeasonScores?.[Constants.mythicPlusSeason] || 0;
                                if (match[2] && match[3]) {
                                    return compareValues(
                                        match[2].toString(),
                                        mythicPlusScore,
                                        parseInt(match[3])
                                    );
                                }
                                return mythicPlusScore > 0;
                            }

                            // Profession slug
                            const slugProfession = wowthingData.static.professionBySlug.get(
                                professionSlugMap[part] || part
                            );
                            if (slugProfession) {
                                return !!char.professions?.[slugProfession.id];
                            }

                            // Profession type
                            if (part.match(/^(craft|crafter|crafting)$/)) {
                                return Object.keys(char.professions || {}).some(
                                    (professionId) => isCraftingProfession[parseInt(professionId)]
                                );
                            }
                            if (part.match(/^(gather|gatherer|gathering)$/)) {
                                return Object.keys(char.professions || {}).some(
                                    (professionId) => isGatheringProfession[parseInt(professionId)]
                                );
                            }

                            // Profession collector
                            if (part === 'collector') {
                                return Object.values(
                                    settingsState.value.professions.collectingCharactersV2 || {}
                                ).some((characterIds) => characterIds.includes(char.id));
                            }

                            match = part.match(/^collector:(\S+)$/);
                            if (match) {
                                const professionId = professionSlugToId[match[1].toString()];
                                return settingsState.value.professions.collectingCharactersV2[
                                    professionId
                                ]?.includes(char.id);
                            }

                            // Work orders available?
                            // if (part === 'orders') {
                            //     return lazyStore.characters[char.id].professionWorkOrders.have > 0;
                            // }

                            return false;
                        })(outerPart.replace(/^!/, '')) === outerPart.startsWith('!')
                            ? false
                            : true)
            )
        );
    }

    return filterFunc ? filterFunc(char) && result : result;
}

function compareValues(comparison: string, sourceValue: number, compareValue: number): boolean {
    if (comparison === '<') return sourceValue < compareValue;
    else if (comparison === '<=') return sourceValue <= compareValue;
    else if (comparison === '=') return sourceValue === compareValue;
    else if (comparison === '>=') return sourceValue >= compareValue;
    else if (comparison === '>') return sourceValue > compareValue;
}

const professionSlugMap: Record<string, string> = {
    alc: 'alchemy',
    alch: 'alchemy',
    alchemist: 'alchemist',
    blacksmith: 'blacksmithing',
    bs: 'blacksmithing',
    cook: 'cooking',
    ench: 'enchanting',
    enchant: 'enchanting',
    eng: 'engineering',
    engi: 'engineering',
    engineer: 'engineering',
    engy: 'engineering',
    herb: 'herbalism',
    insc: 'inscription',
    jc: 'jewelcrafting',
    jewel: 'jewelcrafting',
    mine: 'mining',
    scribe: 'inscription',
    skin: 'skinning',
    smith: 'blacksmithing',
    tailor: 'tailoring',
};
